import type { Wine } from '@/types';
import { formatGrapeDisplay } from '@/utils/grapeUtils';
import { Capacitor } from '@capacitor/core';
import { Filesystem, Directory, Encoding } from '@capacitor/filesystem';
import { Share as CapShare } from '@capacitor/share';

function buildCSV(inventory: Wine[]): string {
  const headers = [
    'Producer', 'Wine Name', 'Vintage', 'Type', 'Cepage',
    'Region', 'Country', 'Appellation', 'Location', 'Quantity', 'Price',
    'Rating', 'Maturity', 'Drink From', 'Drink Until',
    'Tasting Notes', 'Personal Note', 'Format',
  ];

  const escapeCSV = (value: any): string => {
    const str = String(value ?? '');
    if (str.includes(',') || str.includes('"') || str.includes('\n')) {
      return `"${str.replace(/"/g, '""')}"`;
    }
    return str;
  };

  const rows = inventory.map(w => [
    w.producer, w.name, w.vintage || '', w.type || '', formatGrapeDisplay(w.grapeVarieties) || '',
    w.region || '', w.country || '', w.appellation || '', w.location || '', w.quantity || '',
    w.price || '', w.criticRating || '', w.maturity || '', w.drinkFrom || '',
    w.drinkUntil || '', w.tastingNotes || '', w.personalNote || '', w.format || '',
  ].map(escapeCSV).join(','));

  // BOM prefix so Excel reads UTF-8 correctly
  return '\uFEFF' + [headers.join(','), ...rows].join('\n');
}

export async function exportCellarCSV(inventory: Wine[]): Promise<void> {
  const csv = buildCSV(inventory);
  const filename = `rave-cave-cellar-${new Date().toISOString().split('T')[0]}.csv`;

  if (Capacitor.isNativePlatform()) {
    // Android WebView ignores the `<a download>` trick \u2014 write to filesystem and surface via Share sheet.
    const written = await Filesystem.writeFile({
      path: filename,
      data: csv,
      directory: Directory.Cache,
      encoding: Encoding.UTF8,
    });
    await CapShare.share({
      title: 'Rave Cave cellar export',
      url: written.uri,
      dialogTitle: 'Save or share your cellar CSV',
    });
    return;
  }

  // Web: classic blob + download
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}
